import { createMemo, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Flame } from "@fleet/icons";
import { computeStreaks, localDateKey } from "@journal/shared";
import { useEntryDaysQuery } from "@journal/shared/solid";
import { useI18n } from "@/providers/i18n-provider";
import { useJournalData } from "../hooks/use-journal-data";

/** Streak of days writing (current + longest), calculated locally. */
export function StreakBadge() {
  const { t } = useI18n();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const days = useEntryDaysQuery({ db, drizzle, userId, staleTime });

  const streaks = createMemo(() => {
    const dates = (days.data ?? []).map((d) => d.entry_date).filter((d): d is string => Boolean(d));
    return computeStreaks(dates, localDateKey());
  });

  return (
    <Show when={streaks().current > 0 || streaks().longest > 0}>
      <div
        class="flex items-center gap-2 rounded-full border border-border/40 bg-card/60 px-3 py-1.5"
        data-testid="streak-badge"
        title={t(msg`Longest streak: {count} days`, { count: streaks().longest })}
      >
        <Flame class="h-4 w-4 text-orange-400" />
        <span class="text-sm font-semibold text-foreground" data-testid="streak-current">
          {t(msg`{count} day streak`, { count: streaks().current })}
        </span>
        <Show when={!streaks().wroteToday && streaks().current > 0}>
          <span class="text-[11px] text-muted-foreground/70">
            {t(msg`Write today to keep it!`)}
          </span>
        </Show>
      </div>
    </Show>
  );
}
